import DigiobcanApi, { type VotingDetail } from '../../../../api/DigiobcanApi';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import {
  QuestionContentSummary,
  QuestionSummaryCard,
} from '../../../../components/Votings/QuestionSummary';
import QuestionTypeIcon from '../../../../components/Votings/QuestionTypeIcon';
import VotingResultsView from '../../../../components/Votings/VotingResultsView';
import { getVotingResults } from '../../../../features/queries/getVotingResults';
import { shortId } from '../../../../lib/ids';
import { QueryNameSpace, useM, useQ } from '../../../../lib/query';
import { questionSummary, votingState } from '../../../../lib/votings';
import { useLogger } from '../../../../providers/LoggerProvider';
import { useResponsive } from '../../../../providers/ResponsiveProvider';
import {
  __,
  formatDateTime,
  toastError,
  toastSuccess,
} from '../../../../utils';
import VotingAdminSummary, {
  SUMMARY_RAIL_WIDTH,
  type VotingVisibilityPatch,
} from './VotingAdminSummary';
import VotingReportDialog from './VotingReportDialog';
import VotingScheduleDialog from './VotingScheduleDialog';
import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Box,
  Button,
  Card,
  Skeleton,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

type Props = {
  voting: VotingDetail;
  onChanged: () => Promise<unknown>;
};

/**
 * Hlasovanie, ktorého **obsah** sa už nedá upraviť - otvorené, uzavreté alebo
 * archivované. Zamknuté sú otázky, možnosti, publikum a dátum otvorenia;
 * viditeľnosť a na bežiacom hlasovaní aj termín uzavretia sa menia práve tu
 * (každé vlastným endpointom, lebo `PUT` na hlasovaní po otvorení odmieta
 * `VOTING_ALREADY_ACTIVE`).
 *
 * Je to **zápis, nie vypnutý formulár**. Predtým tu stál modrý pás „nie je
 * možné upravovať" nad formulárom, ktorý mal šedé polia; úradník tak čítal
 * hlasovanie cez rozhranie na jeho písanie.
 *
 * **Výsledky sú tu, nie za tabom.** Kým to boli dva taby („Hlasovanie"
 * a „Výsledky"), tá istá vec bola napísaná dvakrát - počet hlasov, publikum
 * a zoznam otázok stáli v oboch, každý raz inak zaokrúhlené - a úradník musel
 * prepínať medzi číslom a jeho vysvetlením.
 *
 * **Rozloženie je flush pás vľavo + plocha vpravo**, tá istá vizuálna reč ako
 * profil a builder hlasovania - nie plávajúca karta v mriežke. Vľavo je
 * hlasovanie ako celok a jeho akcie ([[VotingAdminSummary]]: názov, popis,
 * čísla, termín uzavretia, uzavretie, export, archivácia), vpravo pruh stavu
 * a pod ním výsledky alebo obsah. Uzavretie stojí v tom páse ako výrazné
 * tlačidlo, archivácia na jeho spodku za linkou - je nevratná a nie je to bežný
 * krok životného cyklu.
 */
const VotingLockedView: React.FC<Props> = ({ voting, onChanged }) => {
  const { logger } = useLogger();
  const queryClient = useQueryClient();
  const { isLg } = useResponsive();
  // Hodiny pri každom renderi, nie raz pri mountnutí - inak pruh stavu po
  // uzavretí hlasovania ešte hlási „prebieha"; prečo, je v [[VotingDetailAdminPage]].
  const now = new Date();
  const state = votingState(voting, now);

  const [confirm, setConfirm] = React.useState<'close' | 'archive' | null>(
    null,
  );
  const [isScheduleOpen, setIsScheduleOpen] = React.useState(false);
  const [isReportOpen, setIsReportOpen] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const isArchived = state === 'archived';

  /**
   * **Termín uzavretia sa dá zmeniť, kým hlasovanie beží** - posunúť, skrátiť
   * alebo zrušiť celkom. Je to spolu s viditeľnosťou jediné, čo sa na otvorenom
   * hlasovaní mení; obsah je od publikovania zamknutý a `open_from` sa nemení
   * vôbec (hlasovanie už beží a hlasy sa k jeho začiatku vzťahujú).
   *
   * Na uzavretom hlasovaní akcia **nie je**: server ju odmietne
   * (`VOTING_NOT_OPEN`), lebo posunutie konca do budúcnosti by hlasovanie
   * znovu otvorilo - a to nie je zmena termínu, ale nové rozhodnutie o už
   * zverejnených výsledkoch. Draft mení dátumy vo formulári nastavení.
   */
  const canEditSchedule = state === 'open';

  // Uzavrieť sa dá len otvorené hlasovanie **bez dátumu konca** - s dátumom
  // sa zavrie samo, ručná akcia by klamala, že bez nej nezavrie.
  const canClose = state === 'open' && !voting.open_to;

  /**
   * **Archivované hlasovanie výsledky nevydá a je to zámer servera.**
   * `getVotingResults` má `where secret is not null`, takže na archivované
   * vracia 404 - agregáty v databáze zostanú, ale endpoint ich neukáže
   * (`digiobcan-api`, `votings.ctrl.ts`). Dotaz sa preto nepúšťa vôbec: inak
   * z toho bola len červená hláška „nepodarilo sa načítať", ktorá vyzerá ako
   * porucha, hoci je to dôsledok archivácie.
   *
   * **Inak už úradník výsledky vidí vždy** - aj kým hlasovanie beží
   * s vypnutými „Priebežnými výsledkami", aj keď má vypnutý „Prístup
   * k výsledkom". Obe nastavenia hovoria o tom, čo uvidí **občan**;
   * `shouldIncludeQuestions` v API preto prepustí každého s právom
   * `pruser:votings:manage`, a do tejto obrazovky sa iný ako on nedostane
   * (`PermissionsGuard` nad routou v [[App]]). Kým to tak nebolo, tá istá
   * obrazovka mala pre otvorené hlasovanie vlastnú vysvetľujúcu kartu
   * namiesto výsledkov.
   */
  const detailedAvailable = !isArchived;

  const {
    data: results = [],
    isLoading: isLoadingResults,
    isError: isErrorResults,
  } = useQ(
    getVotingResults,
    { votingId: voting.voting_id },
    { enabled: detailedAvailable },
  );

  React.useEffect(() => {
    if (isErrorResults) {
      toastError(__('Výsledky hlasovania sa nepodarilo načítať.'));
    }
  }, [isErrorResults]);

  const closeMutation = useM(
    () =>
      DigiobcanApi.adminUpdateVotingStatus(voting.voting_id, undefined, {
        status: 'closed',
      }),
    {
      onSuccess: async () => {
        toastSuccess(__('Hlasovanie bolo uzavreté.'));
        await onChanged();
      },
      onError: (e) => {
        logger.error('Error closing voting', {
          action: 'closeVoting',
          error: e,
          data: { votingId: voting.voting_id },
        });
        toastError(__('Hlasovanie sa nepodarilo uzavrieť.'));
      },
    },
  );

  /**
   * Prepnutie viditeľnosti - **optimisticky**, aby prepínač sadol na klik
   * a nie až po odpovedi servera.
   *
   * Zapisuje sa priamo do cache detailu, nie do lokálneho stavu: `voting` je
   * prop z [[VotingDetailAdminPage]], takže lokálny stav by sa pri prvom
   * refetchi prepísal späť a prepínač by preskočil. Kľúč je ten istý, aký
   * skladá stránka (`useQ(adminGetVotingDetail, { votingId })`), a
   * `voting.voting_id` je appková podoba ID - tá istá, akú stránka dostane
   * z adresy cez `entityId`.
   *
   * Pri chybe sa vráti predchádzajúca hodnota z `onMutate`; server je zdrojom
   * pravdy pre koncový stav, preto sa po úspechu prepíše tým, čo odpovedal
   * (vypnutý prístup k výsledkom strhne aj priebežné).
   */
  const detailQueryKey = React.useMemo(
    () => [QueryNameSpace.adminGetVotingDetail, { votingId: voting.voting_id }],
    [voting.voting_id],
  );

  const visibilityMutation = useM<
    Awaited<ReturnType<typeof DigiobcanApi.adminUpdateVotingVisibility>>,
    { details: { name?: string } | null },
    VotingVisibilityPatch,
    { previous?: VotingDetail }
  >(
    (patch) =>
      DigiobcanApi.adminUpdateVotingVisibility(
        voting.voting_id,
        undefined,
        patch,
      ),
    {
      onMutate: async (patch) => {
        // Bežiaci refetch detailu by po našom zápise dosadol so starou
        // hodnotou a prepínač by sa vrátil.
        await queryClient.cancelQueries({ queryKey: detailQueryKey });

        const previous = queryClient.getQueryData<VotingDetail>(detailQueryKey);

        queryClient.setQueryData<VotingDetail>(detailQueryKey, (old) =>
          old ? { ...old, ...patch } : old,
        );

        return { previous };
      },
      onSuccess: (updated) => {
        queryClient.setQueryData<VotingDetail>(detailQueryKey, (old) =>
          old ? { ...old, ...updated } : old,
        );
        // Občianske zoznamy, detail aj výsledky ukazujú to isté rozhodnutie.
        void onChanged();
      },
      onError: (e, patch, context) => {
        if (context?.previous) {
          queryClient.setQueryData(detailQueryKey, context.previous);
        }
        logger.error('Error updating voting visibility', {
          action: 'updateVotingVisibility',
          error: e,
          data: { votingId: voting.voting_id, patch },
        });
        toastError(__('Viditeľnosť hlasovania sa nepodarilo zmeniť.'));
      },
    },
  );

  const handleVisibilityChange = (patch: VotingVisibilityPatch) => {
    if (!navigator.onLine) {
      toastError(__('Nie ste pripojený k internetu.'));
      return;
    }
    visibilityMutation.mutate(patch);
  };

  /**
   * Zmena termínu uzavretia. Telo je **celý rozvrh**, nie čiastočná zmena:
   * chýbajúci `open_to` znamená „bez termínu", takže sa tou istou požiadavkou
   * termín posúva aj ruší (`PUT /admin/votings/{votingId}/schedule`).
   *
   * Optimisticky sa nezapisuje - na rozdiel od prepínačov viditeľnosti tu
   * zmena prekresľuje stav hlasovania (pruh, „Uzavrieť hlasovanie", stĺpec
   * stavu v zozname), takže polovica obrazovky by chvíľu tvrdila niečo, čo
   * server ešte nepotvrdil. Dialóg počká na odpoveď a zavrie sa až po nej.
   */
  const scheduleMutation = useM(
    (openTo: Date | null) =>
      DigiobcanApi.adminUpdateVotingSchedule(
        voting.voting_id,
        undefined,
        openTo ? { open_to: openTo.toISOString() } : {},
      ),
    {
      onSuccess: async () => {
        toastSuccess(__('Termín uzavretia bol zmenený.'));
        setIsScheduleOpen(false);
        await onChanged();
      },
      onError: (e) => {
        logger.error('Error updating voting schedule', {
          action: 'updateVotingSchedule',
          error: e,
          data: { votingId: voting.voting_id },
        });
        toastError(__('Termín uzavretia sa nepodarilo zmeniť.'));
      },
    },
  );

  /**
   * Nastavenie reportu. Telo je **celé nastavenie**, nie čiastočná zmena -
   * `PUT /admin/votings/{id}/report` nahrádza zoznam adries celý, rovnako ako
   * `/schedule` nahrádza celý rozvrh.
   *
   * Neoptimisticky, z rovnakého dôvodu ako termín: mení riadok v páse aj stav
   * odoslania, takže by časť obrazovky chvíľu tvrdila niečo, čo server ešte
   * nepotvrdil. Dialóg počká na odpoveď a zavrie sa až po nej.
   */
  const reportMutation = useM(
    (patch: { enabled: boolean; emails: string[] }) =>
      DigiobcanApi.adminUpdateVotingReport(voting.voting_id, undefined, {
        report_enabled: patch.enabled,
        report_emails: patch.emails,
      }),
    {
      onSuccess: async () => {
        toastSuccess(__('Nastavenie reportu bolo uložené.'));
        setIsReportOpen(false);
        await onChanged();
      },
      onError: (e) => {
        logger.error('Error updating voting report settings', {
          action: 'updateVotingReport',
          error: e,
          data: { votingId: voting.voting_id },
        });
        toastError(__('Nastavenie reportu sa nepodarilo uložiť.'));
      },
    },
  );

  /**
   * „Poslať report teraz". Server razítko `report_sent_at` nastaví **len na
   * uzavretom hlasovaní** - na bežiacom by tým plánovanú úlohu po uzavretí
   * preskočil a mesto by zostalo s priebežnými číslami namiesto konečných.
   */
  const sendReportMutation = useM(
    () => DigiobcanApi.adminSendVotingReport(voting.voting_id, {}),
    {
      onSuccess: async (result) => {
        toastSuccess(
          `${__('Report bol odoslaný na')} ${result.recipients} ${__('adries.')}`,
        );
        await onChanged();
      },
      onError: (e) => {
        logger.error('Error sending voting report', {
          action: 'sendVotingReport',
          error: e,
          data: { votingId: voting.voting_id },
        });
        toastError(__('Report sa nepodarilo odoslať.'));
      },
    },
  );

  /**
   * Stiahnutie PDF. **Ide mimo `useM`**, lebo odpoveď nie je JSON: generovaný
   * klient pri inom `Content-Type` vráti samotný `Response`, z ktorého sa
   * spraví `Blob` a odkaz - tá istá cesta, akou sa sťahuje CSV, len obsah
   * prichádza zo servera.
   */
  const handleDownloadReport = async (format: 'pdf' | 'csv') => {
    if (!navigator.onLine) {
      toastError(__('Nie ste pripojený k internetu.'));
      return;
    }

    setIsDownloading(true);
    try {
      const response = (await DigiobcanApi.adminDownloadVotingReport(
        voting.voting_id,
        { format },
      )) as unknown as Response;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hlasovanie-${shortId(voting.voting_id)}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      logger.error('Error downloading voting report', {
        action: 'downloadVotingReport',
        error: e,
        data: { votingId: voting.voting_id, format },
      });
      toastError(__('Report sa nepodarilo stiahnuť.'));
    } finally {
      setIsDownloading(false);
    }
  };

  const archiveMutation = useM(
    () => DigiobcanApi.adminForgetVoting(voting.voting_id, {}),
    {
      onSuccess: async () => {
        toastSuccess(__('Hlasovanie bolo archivované.'));
        await onChanged();
      },
      onError: (e) => {
        logger.error('Error archiving voting', {
          action: 'archiveVoting',
          error: e,
          data: { votingId: voting.voting_id },
        });
        toastError(__('Hlasovanie sa nepodarilo archivovať.'));
      },
    },
  );

  /**
   * **Layout je flush pás vľavo + plocha vpravo**, tá istá reč ako profil
   * a builder hlasovania - nie plávajúca karta v mriežke. Pás (súhrn a akcie)
   * je pevný stĺpec s deliacou linkou vpravo a scrolluje sa sám; vpravo je
   * pruh stavu a pod ním výsledky alebo obsah na plátne. Celý pohľad vypĺňa
   * výšku ako builder (`height: 100%`), takže sa oba stĺpce skrolujú
   * nezávisle. Na mobile (`!isLg`) je z pásu karta a všetko ide pod seba.
   *
   * „Uzavrieť hlasovanie" je preto **v tom páse** (výrazné tlačidlo pod
   * údajmi), nie v pätičke cez celú šírku, kde chvíľu bolo.
   */
  const summary = (variant: 'rail' | 'card', withClose: boolean) => (
    <VotingAdminSummary
      variant={variant}
      voting={voting}
      results={results}
      isArchived={isArchived}
      onVisibilityChange={handleVisibilityChange}
      // Na desktope „Uzavrieť" nesie pripnutá pätička pravej plochy (nižšie),
      // nie pás - tam by pretiekla cez sidebar. Mobilná karta ho má v sebe.
      onClose={withClose && canClose ? () => setConfirm('close') : undefined}
      onArchive={() => setConfirm('archive')}
      onEditSchedule={
        canEditSchedule ? () => setIsScheduleOpen(true) : undefined
      }
      // Report sa dá nastaviť na bežiacom aj uzavretom hlasovaní; archivované
      // výsledky nemá, takže by report nemal čo vygenerovať.
      onEditReport={isArchived ? undefined : () => setIsReportOpen(true)}
      onDownloadReport={isArchived ? undefined : handleDownloadReport}
      onSendReport={
        isArchived
          ? undefined
          : () => {
              if (!navigator.onLine) {
                toastError(__('Nie ste pripojený k internetu.'));
                return;
              }
              sendReportMutation.mutate(undefined);
            }
      }
      sendingReport={sendReportMutation.isPending}
      downloadingReport={isDownloading}
    />
  );

  // Farba pruhu podľa stavu - modrá „prebieha", zelená „uzavreté", červená
  // „archivované". Predtým bol biely s tenkým rámom a strácal sa; týchto správ
  // je viac a majú byť na prvý pohľad rozoznateľné.
  const stripeTone: 'info' | 'success' | 'error' = isArchived
    ? 'error'
    : state === 'closed'
      ? 'success'
      : 'info';

  const statusStripe = (
    <Stack
      direction='row'
      alignItems='center'
      gap={1.5}
      sx={({ palette, shape }) => ({
        border: `1px solid ${palette[stripeTone].light}`,
        borderRadius: `${shape.borderRadius}px`,
        backgroundColor: palette[stripeTone].surface,
        color: palette[stripeTone].extraDark,
        px: 2,
        py: 1.5,
      })}
    >
      <Box
        sx={({ palette, shape }) => ({
          width: 24,
          height: 24,
          flex: 'none',
          display: 'grid',
          placeItems: 'center',
          borderRadius: `${shape.borderRadiusFull}px`,
          backgroundColor: palette[stripeTone].main,
          color: palette.common.white,
        })}
      >
        {state === 'closed' ? (
          <CheckIcon sx={{ fontSize: 14 }} />
        ) : (
          <LockOutlinedIcon sx={{ fontSize: 14 }} />
        )}
      </Box>
      <Typography
        sx={{ fontSize: 15, fontWeight: 500, lineHeight: 1.5, flex: 1 }}
      >
        {isArchived
          ? __(
              'Hlasovanie je archivované. Občania ho nevidia a obnoviť sa už nedá.',
            )
          : state === 'closed'
            ? `${__('Uzavreté')}${voting.open_to ? ` ${formatDateTime(voting.open_to)}` : ''}. ${
                voting.available_results
                  ? __('Podrobné výsledky sú odteraz verejné.')
                  : __('Podrobné výsledky zostávajú neverejné.')
              }`
            : /* Pri otvorenom hlasovaní pruh nesmie hlásiť len „nedá sa
                 upraviť": zamknutý je jeho **obsah**, kým termín uzavretia
                 a viditeľnosť sa menia práve teraz - a keby o tom pruh mlčal,
                 odkaz „Zmeniť" v páse by vyzeral ako omyl. */
              `${__('Hlasovanie je otvorené')}${voting.open_from ? ` ${__('od')} ${formatDateTime(voting.open_from)}` : ''}${
                voting.open_to
                  ? ` ${__('a zavrie sa')} ${formatDateTime(voting.open_to)}`
                  : ` ${__('a zavrie sa až ručne')}`
              }. ${__('Obsah sa už upraviť nedá, termín uzavretia a viditeľnosť áno.')}`}
      </Typography>
    </Stack>
  );

  const resultsContent = (
    <Stack gap={1.5} sx={{ minWidth: 0 }}>
      {/* Výsledky. Jediný stav, v ktorom ich server nevydá ani úradníkovi, je
            archivácia - a ten má vlastnú vetu, lebo prázdna plocha by sa
            čítala ako porucha. */}
      {isArchived ? (
        <Card sx={{ p: 3, gap: 1.25, alignItems: 'flex-start' }}>
          <Typography component='b' sx={{ fontSize: 17, fontWeight: 700 }}>
            {__('Podrobné výsledky archivovaného hlasovania sa nezobrazujú')}
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            {__(
              'Archivácia zmazala voľné odpovede aj záznamy o účasti a hlasovanie sa od nej nedá otvoriť ani cez priamy odkaz - vrátane jeho výsledkov. Zostáva počet hlasov vľavo.',
            )}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {__(
              'Ak sú výsledky potrebné pre komisiu, exportujte ich do CSV skôr, než hlasovanie archivujete.',
            )}
          </Typography>
        </Card>
      ) : isLoadingResults ? (
        <Stack gap={1.5}>
          <Skeleton variant='rounded' height={220} />
          <Skeleton variant='rounded' height={220} />
        </Stack>
      ) : (
        <>
          {/* Úradník tie isté čísla vidí aj vtedy, keď ich občan nevidí -
              nech to vie, skôr než ich niekam prenesie. */}
          {(!voting.available_results ||
            (state === 'open' && !voting.available_incomplete_results)) && (
            <Typography variant='body2' color='text.secondary' sx={{ px: 0.5 }}>
              {!voting.available_results
                ? __(
                    'Tieto výsledky občan nevidí - hlasovanie ich nezverejňuje ani po uzavretí.',
                  )
                : __(
                    'Tieto výsledky občan zatiaľ nevidí - hlasovanie zverejní podrobnosti až po uzavretí.',
                  )}
            </Typography>
          )}
          <VotingResultsView
            questions={results}
            votesCount={voting.votes_count}
            tone='admin'
          />
        </>
      )}

      {/* Obsah hlasovania má zmysel len tam, kde ho výsledky nevypisujú samé
            - inak by tá istá otázka stála na obrazovke dvakrát.

            **Je to karta na otázku, nie jedna karta so zoznamom** a nesie ju
            ten istý komponent ako kontrolu pred odoslaním u občana
            ([[QuestionSummary]]). Predtým tu bol na otázku jeden riadok
            v tvare „Škála 1 - 5 · 26 tvrdení · Prvé, Druhé a ďalších 24",
            z ktorého sa nedalo prečítať ani jedno celé tvrdenie - a práve
            tvrdenia sú to, čo úradník po otvorení hlasovania kontroluje.
            Karty stoja na plátne, nie v ďalšej karte: rám v ráme je karta
            v karte. */}
      {(isArchived || !detailedAvailable) && (
        <Stack gap={1.25}>
          <Typography
            component='b'
            sx={{ fontSize: 17, fontWeight: 700, px: 0.5 }}
          >
            {__('Obsah hlasovania')}
          </Typography>

          {voting.questions.map((question, index) => (
            <QuestionSummaryCard
              key={question.question_id}
              mark={
                <Box
                  sx={({ palette, shape }) => ({
                    width: 26,
                    height: 26,
                    flex: 'none',
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: `${shape.borderRadiusSm + 2}px`,
                    backgroundColor: palette.grey[100],
                    color: palette.grey[600],
                  })}
                >
                  <QuestionTypeIcon type={question.type} size={14} />
                </Box>
              }
              meta={`${__('Otázka')} ${index + 1} · ${questionSummary(question)}`}
              title={question.title}
            >
              <QuestionContentSummary question={question} />
            </QuestionSummaryCard>
          ))}
        </Stack>
      )}
    </Stack>
  );

  return (
    <>
      {isLg ? (
        <Stack
          direction='row'
          sx={{
            height: '100%',
            minHeight: 0,
            backgroundColor: 'background.paper',
          }}
        >
          {/* Ľavý pás - flush, deliaca linka vpravo, scrolluje sa sám. */}
          <Stack
            sx={{
              flex: 'none',
              width: SUMMARY_RAIL_WIDTH,
              minHeight: 0,
              overflowY: 'auto',
              borderRight: '1px solid',
              borderColor: 'divider',
            }}
          >
            {summary('rail', false)}
          </Stack>

          {/* Pravá plocha - skrolovací obsah a pod ním **pripnutá pätička len
              tejto plochy**: „Uzavrieť" tak nepretečie cez sidebar vľavo. */}
          <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <Stack
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                gap: 1.5,
                p: 1.5,
                backgroundColor: 'background.canvas',
              }}
            >
              {statusStripe}
              {resultsContent}
            </Stack>

            {canClose && (
              <Stack
                direction='row'
                sx={{
                  flex: 'none',
                  p: 1.5,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                }}
              >
                <Button
                  variant='dark'
                  onClick={() => setConfirm('close')}
                  sx={{ ml: 'auto' }}
                >
                  {__('Uzavrieť hlasovanie')}
                </Button>
              </Stack>
            )}
          </Stack>
        </Stack>
      ) : (
        <Stack sx={{ gap: 1.5, p: 1.5, backgroundColor: 'background.canvas' }}>
          {statusStripe}
          {summary('card', true)}
          {resultsContent}
        </Stack>
      )}

      <VotingScheduleDialog
        open={isScheduleOpen}
        openFrom={voting.open_from}
        openTo={voting.open_to}
        loading={scheduleMutation.isPending}
        onCancel={() => setIsScheduleOpen(false)}
        onConfirm={(openTo) => {
          if (!navigator.onLine) {
            toastError(__('Nie ste pripojený k internetu.'));
            return;
          }
          scheduleMutation.mutate(openTo);
        }}
      />

      <VotingReportDialog
        open={isReportOpen}
        reportEnabled={!!voting.report_enabled}
        reportEmails={voting.report_emails ?? []}
        isOver={state === 'closed'}
        loading={reportMutation.isPending}
        onCancel={() => setIsReportOpen(false)}
        onConfirm={(patch) => {
          if (!navigator.onLine) {
            toastError(__('Nie ste pripojený k internetu.'));
            return;
          }
          reportMutation.mutate(patch);
        }}
      />

      <ConfirmationDialog
        open={confirm === 'close'}
        title={__('Uzavrieť hlasovanie?')}
        description={__(
          'Po uzavretí nebudú môcť občania hlasovať. Naozaj chcete pokračovať?',
        )}
        confirmLabel={__('Uzavrieť hlasovanie')}
        isLoading={closeMutation.isPending}
        onConfirm={async () => {
          try {
            await closeMutation.mutateAsync();
          } catch {
            // Chybu hlási mutácia.
          }
          setConfirm(null);
        }}
        onClose={() => setConfirm(null)}
      />

      <ConfirmationDialog
        open={confirm === 'archive'}
        title={__('Archivovať hlasovanie?')}
        description={__(
          'Hlasovanie bude archivované a NIKDY ho už nebude možné obnoviť. Zmažú sa voľné odpovede aj záznamy o účasti a podrobné výsledky sa prestanú zobrazovať - ak ich potrebujete, najprv si ich vyexportujte do CSV. Naozaj chcete pokračovať?',
        )}
        confirmLabel={__('Archivovať hlasovanie')}
        isLoading={archiveMutation.isPending}
        onConfirm={async () => {
          try {
            await archiveMutation.mutateAsync();
          } catch {
            // Chybu hlási mutácia.
          }
          setConfirm(null);
        }}
        onClose={() => setConfirm(null)}
      />
    </>
  );
};

export default VotingLockedView;
